import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BarChart3,
  ChefHat,
  Bell,
  BookOpen,
  Contact,
  Boxes,
  Home,
  KeyRound,
  LogOut,
  MonitorSmartphone,
  MoreHorizontal,
  PackageX,
  Printer,
  ReceiptText,
  Settings,
  ShieldCheck,
  ShoppingCart,
  Store,
  Users,
  Users2,
  Wallet,
  X,
} from 'lucide-react';
import { BRAND } from '@shripos/shared/brand';
import { Spinner, cn, formatDate, timeAgo } from '@shripos/ui';
import { api, logout } from '../lib/api.js';
import { useRealtime } from '../lib/realtime.js';
import { useOfflineReady, useOnlineResume } from '../lib/offline/index.js';
import { useOffline } from '../stores/offline.js';
import { LanguagePicker } from './LanguagePicker.jsx';
import { SyncStatus } from './SyncStatus.jsx';
import { can, kitchenOf, useSession } from '../stores/session.js';

const NAV = [
  { to: '/', key: 'home', label: 'Home', icon: Home, end: true },
  { to: '/pos', key: 'pos', label: 'Billing', icon: ShoppingCart, perm: 'pos.bill' },
  { to: '/bills', key: 'bills', label: 'Bills', icon: ReceiptText, perm: 'pos.bill' },
  { to: '/kds', key: 'kds', label: 'Kitchen', icon: ChefHat, perm: 'kds.use', kitchen: 'display' },
  { to: '/menu', key: 'menu', label: 'Menu', icon: BookOpen, perm: 'menu.edit' },
  { to: '/stock', key: 'stock', label: 'Availability', icon: PackageX, perm: 'menu.availability' },
  { to: '/reports', key: 'reports', label: 'Reports', icon: BarChart3, perm: 'reports.view' },
  { to: '/customers', key: 'customers', label: 'Customers', icon: Contact, perm: 'customers.view', feature: 'crm' },
  { to: '/inventory', key: 'inventory', label: 'Inventory', icon: Boxes, perm: 'stock.view' },
  { to: '/cash', key: 'cash', label: 'Cash', icon: Wallet, perm: 'shift.manage' },
  { to: '/staff', key: 'staff', label: 'Staff', icon: Users, perm: 'staff.manage' },
  { to: '/roles', key: 'roles', label: 'Roles', icon: ShieldCheck, perm: 'staff.manage' },
  { to: '/devices', key: 'devices', label: 'Devices', icon: MonitorSmartphone, perm: 'devices.manage' },
  { to: '/printers', key: 'printers', label: 'Printers', icon: Printer, perm: 'printers.manage' },
  { to: '/outlets', key: 'outlets', label: 'Outlets', icon: Store },
  { to: '/settings', key: 'settings', label: 'Settings', icon: Settings, perm: 'settings.manage' },
];

/** Expiry / grace / expired banner (from the shared license rules). */
/** Read-only support session opened from the admin portal ("View as client"). */
function SupportViewBanner({ session }) {
  if (!session?.readOnly) return null;
  const until = new Date(session.expiresAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  return (
    <div
      className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-violet-700 px-4 py-2 text-center text-sm text-white"
      role="status"
    >
      <span>
        <strong>Support view</strong> by {session.impersonatedBy} — read-only, ends at {until}. Changes and exports are
        switched off.
      </span>
      <button
        type="button"
        onClick={() => logout()}
        className="rounded bg-white/15 px-2 py-0.5 font-medium hover:bg-white/25"
      >
        Exit
      </button>
    </div>
  );
}

function LicenseBanner({ license }) {
  const { t } = useTranslation();
  if (!license?.banner) return null;
  const { key, tone } = license.banner;
  let text;
  if (key === 'expiring')
    text =
      license.daysLeft <= 0
        ? t('license.expiringToday')
        : t('license.expiring', { days: license.daysLeft, date: formatDate(license.expiryDate) });
  else if (key === 'grace') text = t('license.grace', { days: Math.max(0, license.graceDaysLeft) });
  else if (key === 'expired') text = t('license.expired');
  else return null;
  return (
    <div
      className={cn(
        'px-4 py-2 text-center text-sm font-medium',
        tone === 'warning' ? 'bg-amber-100 text-amber-900' : 'bg-red-600 text-white',
      )}
    >
      {text}
    </div>
  );
}

function Announcements() {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem('shripos.dismissed') ?? '[]');
    } catch {
      return [];
    }
  });
  const { data } = useQuery({
    queryKey: ['announcements'],
    queryFn: () => api.get('/announcements/active'),
    refetchInterval: 10 * 60_000,
  });
  const visible = (data?.items ?? []).filter((a) => !dismissed.includes(a.id));
  if (!visible.length) return null;
  const dismiss = (id) => {
    const next = [...dismissed, id];
    setDismissed(next);
    try {
      sessionStorage.setItem('shripos.dismissed', JSON.stringify(next));
    } catch {
      /* ignore */
    }
  };
  return visible.map((a) => (
    <div
      key={a.id}
      className={cn(
        'flex items-start gap-3 px-4 py-2 text-sm',
        a.severity === 'critical'
          ? 'bg-red-50 text-red-900'
          : a.severity === 'warning'
            ? 'bg-amber-50 text-amber-900'
            : 'bg-sky-50 text-sky-900',
      )}
    >
      <p className="flex-1">
        <strong>{a.title}</strong> — {a.body}
      </p>
      {a.dismissible && (
        <button
          onClick={() => dismiss(a.id)}
          className="rounded p-0.5 opacity-60 hover:opacity-100"
          aria-label={t('shell.dismiss', 'Dismiss')}
        >
          <X className="size-4" />
        </button>
      )}
    </div>
  ));
}

function NotificationsMenu() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.get('/notifications'),
    refetchInterval: 5 * 60_000,
  });
  const readAll = useMutation({
    mutationFn: () => api.post('/notifications/read-all'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
  useEffect(() => {
    const close = (e) => !ref.current?.contains(e.target) && setOpen(false);
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, []);
  const unread = data?.unread ?? 0;
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative rounded-lg p-2 text-slate-600 hover:bg-slate-100"
        aria-label={t('shell.notifications')}
      >
        <Bell className="size-5" />
        {unread > 0 && (
          <span className="absolute top-1 right-1 flex size-4 items-center justify-center rounded-full bg-red-600 text-[10px] font-bold text-white">
            {unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 z-40 mt-2 w-80 max-w-[90vw] rounded-xl bg-white shadow-lg ring-1 ring-slate-200">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
            <p className="text-sm font-semibold">{t('shell.notifications')}</p>
            {unread > 0 && (
              <button onClick={() => readAll.mutate()} className="text-xs text-brand-700 hover:underline">
                {t('shell.markAllRead')}
              </button>
            )}
          </div>
          <ul className="max-h-80 divide-y divide-slate-100 overflow-y-auto">
            {(data?.items ?? []).length === 0 && (
              <li className="px-4 py-6 text-center text-sm text-slate-500">{t('shell.noNotifications')}</li>
            )}
            {(data?.items ?? []).map((n) => (
              <li key={n.id} className={cn('px-4 py-3 text-sm', !n.read && 'bg-brand-50/50')}>
                <p className="font-medium text-slate-900">{n.title}</p>
                {n.body && <p className="text-slate-600">{n.body}</p>}
                <p className="mt-0.5 text-xs text-slate-500">{timeAgo(n.createdAt)}</p>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function UserMenu() {
  const { t } = useTranslation();
  const me = useSession((s) => s.me);
  const device = useSession((s) => s.device);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const close = (e) => !ref.current?.contains(e.target) && setOpen(false);
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, []);
  const item = 'flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-slate-50';
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-lg py-1.5 pr-2 pl-1.5 hover:bg-slate-100"
      >
        <span className="flex size-8 items-center justify-center rounded-full bg-brand-100 text-sm font-semibold text-brand-800">
          {me.user.name.slice(0, 1).toUpperCase()}
        </span>
        <span className="hidden text-left leading-tight md:block">
          <span className="block text-sm font-medium">{me.user.name}</span>
          <span className="block text-xs text-slate-500">{me.user.roleName}</span>
        </span>
      </button>
      {open && (
        <div className="absolute right-0 z-40 mt-2 w-60 overflow-hidden rounded-xl bg-white py-1 shadow-lg ring-1 ring-slate-200">
          {device && (
            <button
              className={item}
              onClick={async () => {
                setOpen(false);
                navigate('/pin');
              }}
            >
              <Users2 className="size-4 text-slate-400" /> {t('pin.switchUser')}
            </button>
          )}
          {me.user.email && (
            <button className={item} onClick={() => (setOpen(false), navigate('/account'))}>
              <KeyRound className="size-4 text-slate-400" /> {t('shell.account')}
            </button>
          )}
          {me.outlets.length > 1 && !me.device && (
            <button className={item} onClick={() => (setOpen(false), navigate('/select-outlet'))}>
              <Store className="size-4 text-slate-400" /> {t('shell.selectOutlet')}
            </button>
          )}
          <div className="border-t border-slate-100 px-4 py-2">
            <p className="mb-1 text-xs text-slate-500">{t('common.language')}</p>
            <LanguagePicker />
          </div>
          <button className={cn(item, 'border-t border-slate-100 text-red-700')} onClick={() => logout()}>
            <LogOut className="size-4" /> {t('common.signOut')}
          </button>
        </div>
      )}
    </div>
  );
}

function MoreSheet({ items, onClose }) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-40 md:hidden" onClick={onClose}>
      <div className="absolute inset-0 bg-slate-900/40" />
      <div
        className="absolute inset-x-0 bottom-0 rounded-t-2xl bg-white p-3 pb-[max(1rem,env(safe-area-inset-bottom))]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="grid grid-cols-4 gap-2">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              onClick={onClose}
              className="flex flex-col items-center gap-1 rounded-xl py-3 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              <n.icon className="size-6" />
              {t(`shell.${n.key}`, n.label)}
            </NavLink>
          ))}
        </div>
      </div>
    </div>
  );
}

export function AppShell() {
  const { t } = useTranslation();
  const me = useSession((s) => s.me);
  const location = useLocation();
  const socketConnected = useRealtime(true);
  useOfflineReady();
  useOnlineResume();
  const offlineSession = useSession((s) => s.offline);
  const reachable = useOffline((s) => s.reachable);
  const [more, setMore] = useState(false);
  const nav = NAV.filter(
    (n) =>
      (!n.perm || can(me, n.perm)) &&
      (!n.feature || me.license?.features?.[n.feature]) &&
      (!n.kitchen || kitchenOf(me)[n.kitchen]),
  );
  const isPos = location.pathname.startsWith('/pos');
  const bottom = nav.length > 5 ? nav.slice(0, 4) : nav;
  const overflow = nav.length > 5 ? nav.slice(4) : [];

  return (
    <div className={cn('flex flex-col', isPos ? 'h-dvh overflow-hidden' : 'min-h-full')}>
      {offlineSession && reachable && (
        // Back online after an offline switch: nothing runs as the previous person meanwhile.
        <div className="fixed inset-0 z-50 flex items-center justify-center gap-3 bg-white/85 text-slate-700">
          <Spinner /> {t('shell.reconnecting', 'Back online: signing in {{name}}…', { name: me.user.name })}
        </div>
      )}
      <SupportViewBanner session={me.session} />
      <LicenseBanner license={me.license} />
      <Announcements />
      <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-3 border-b border-slate-200 bg-white px-3 sm:px-4">
        <NavLink to="/" aria-label={t('shell.home', 'Home')}>
          <img src="/favicon.svg" alt="" className="size-8" />
        </NavLink>
        <div className="min-w-0 flex-1 leading-tight">
          <p className="truncate text-sm font-semibold">{me.tenant.name}</p>
          <p className="truncate text-xs text-slate-500">
            {me.outlet ? me.outlet.name : `${BRAND.productName}`}
            {me.device ? ` · ${me.device.name} (${me.device.code})` : ''}
          </p>
        </div>
        {offlineSession && (
          <span
            className="hidden rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-900 sm:inline"
            title={t(
              'shell.offlineSessionHint',
              "Signed in with a PIN checked on this device. You'll be signed in properly when the internet is back.",
            )}
            data-testid="offline-session"
          >
            {t('shell.offlineSession', 'Signed in offline')}
          </span>
        )}
        <SyncStatus socketConnected={socketConnected} />
        <NotificationsMenu />
        <UserMenu />
      </header>
      <div className="flex min-h-0 flex-1">
        <nav
          className={cn(
            'hidden shrink-0 overflow-y-auto border-r border-slate-200 bg-white md:block',
            isPos ? 'w-16 p-2' : 'w-56 p-3',
          )}
        >
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              title={t(`shell.${n.key}`, n.label)}
              className={({ isActive }) =>
                cn(
                  'mb-1 flex items-center rounded-lg text-sm font-medium',
                  isPos ? 'justify-center p-2.5' : 'gap-3 px-3 py-2.5',
                  isActive ? 'bg-brand-50 text-brand-800' : 'text-slate-600 hover:bg-slate-100',
                )
              }
            >
              <n.icon className="size-5" />
              {!isPos && t(`shell.${n.key}`, n.label)}
            </NavLink>
          ))}
        </nav>
        <main className={cn('min-w-0 flex-1', isPos ? 'min-h-0' : 'px-4 py-5 pb-24 sm:px-6 md:pb-6')}>
          <Outlet />
        </main>
      </div>
      {/* Phone: bottom navigation (the POS screen has its own order bar) */}
      {!isPos && (
        <nav className="fixed inset-x-0 bottom-0 z-30 flex border-t border-slate-200 bg-white pb-[env(safe-area-inset-bottom)] md:hidden">
          {bottom.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                cn(
                  'flex flex-1 flex-col items-center gap-0.5 py-2 text-[11px] font-medium',
                  isActive ? 'text-brand-700' : 'text-slate-500',
                )
              }
            >
              <n.icon className="size-5" />
              {t(`shell.${n.key}`, n.label)}
            </NavLink>
          ))}
          {overflow.length > 0 && (
            <button
              onClick={() => setMore(true)}
              className="flex flex-1 flex-col items-center gap-0.5 py-2 text-[11px] font-medium text-slate-500"
            >
              <MoreHorizontal className="size-5" />
              {t('shell.more', 'More')}
            </button>
          )}
        </nav>
      )}
      {more && <MoreSheet items={overflow} onClose={() => setMore(false)} />}
    </div>
  );
}
